import { useState, useEffect } from 'react';
import { Sparkles, ArrowLeft, X } from 'lucide-react';
import { useCMS } from '../context/CMSContext';

export default function AnnouncementBar() {
  const { content, loading } = useCMS();
  const [isDismissed, setIsDismissed] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  // ─── Version-based dismiss logic ───
  // When admin republishes, announcementVersion bumps → everyone sees it again
  useEffect(() => {
    if (loading) return;
    const currentVersion = content.announcementVersion || 1;
    const dismissedVersion = localStorage.getItem('lobab_ann_dismissed_v');
    if (dismissedVersion && Number(dismissedVersion) >= currentVersion) {
      setIsDismissed(true);
    } else {
      setIsDismissed(false);
    }
  }, [content.announcementVersion, loading]);

  const handleDismiss = () => {
    const currentVersion = content.announcementVersion || 1;
    localStorage.setItem('lobab_ann_dismissed_v', currentVersion);
    setIsDismissed(true);
  };

  // ─── Countdown Timer — Firestore-driven ───
  useEffect(() => {
    if (loading) return;
    const durationDays = Number(content.announcementDurationDays) || 5;
    const durationMs = durationDays * 24 * 60 * 60 * 1000;

    let startMs;
    if (content.announcementStartTime) {
      startMs = new Date(content.announcementStartTime).getTime();
    } else {
      // Fallback: use localStorage if no Firestore start time
      let saved = localStorage.getItem('lobab_ann_start');
      if (saved) {
        startMs = Number(saved);
      } else {
        startMs = Date.now();
        localStorage.setItem('lobab_ann_start', startMs);
      }
    }

    const endTime = startMs + durationMs;

    const updateTimer = () => {
      const diff = endTime - Date.now();
      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / 1000 / 60) % 60),
        seconds: Math.floor((diff / 1000) % 60)
      });
    };

    updateTimer();
    const timerId = setInterval(updateTimer, 1000);
    return () => clearInterval(timerId);
  }, [content.announcementStartTime, content.announcementDurationDays, loading]);

  if (isDismissed || loading) return null;

  return (
    <div className="bg-[#e00000] text-white py-3 px-4 w-full relative overflow-hidden group shadow-md transition-all duration-500 z-[60]">
      <div className="absolute inset-0 bg-white/15 opacity-50 group-hover:opacity-100 animate-pulse transition-opacity duration-700 pointer-events-none"></div>

      <button
        onClick={handleDismiss}
        className="absolute top-2 left-2 z-20 p-1 rounded-full bg-white/10 hover:bg-white/25 transition-colors border border-white/10 text-white/90"
        aria-label="إخفاء الإعلان"
        title="إخفاء الإعلان"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="container mx-auto flex flex-col xl:flex-row items-center justify-center gap-3 xl:gap-8 relative z-10 text-center xl:text-right pr-6 md:pr-0">

        <div className="flex items-center justify-center gap-2 max-w-[90%] md:max-w-full">
          <Sparkles className="w-5 h-5 animate-pulse text-yellow-100 shrink-0 drop-shadow-[0_0_8px_rgba(254,249,195,0.6)] hidden sm:block" />
          <p className="text-lg md:text-base text-white dark:text-white lg:text-lg font-bold drop-shadow-sm">
            <span className="text-yellow-100 font-extrabold ml-1">
              {content.announcementTitle}
            </span>
            {content.announcementText}
            <span className="font-extrabold text-base md:text-lg bg-black/20 px-2 py-0.5 rounded shadow-inner mx-1 border border-white/10">
              {content.announcementPrice}
            </span>
            بدلاً من
            <span className="line-through opacity-90 ml-1">
              {content.announcementOldPrice}
            </span>!
            <span className="xl:inline-block hidden mr-1">ينتهي العرض خلال:</span>
          </p>
        </div>

        <div className="flex flex-col md:flex-row items-center gap-3 mt-1 md:mt-0">
          {/* The Timer */}
          <div className="flex items-center gap-2 md:gap-3 shrink-0 bg-black/20 backdrop-blur-md px-3 md:px-4 py-2 rounded-2xl border border-white/10 shadow-inner" dir="ltr">
            <span className="xl:hidden ml-2 font-bold text-md opacity-90 hidden md:block">ينتهي خلال:</span>

            <div className="flex flex-col items-center">
              <span className="bg-black/20 backdrop-blur-sm px-1.5 md:px-2 py-1 rounded-lg text-base md:text-xl font-extrabold min-w-[2rem] md:min-w-[3rem] tracking-wider text-center shadow-inner border border-white/10 text-yellow-100">
                {String(timeLeft.seconds).padStart(2, '0')}
              </span>
              <span className="text-[9px] md:text-md opacity-90 mt-1 font-bold">ثوانٍ</span>
            </div>

            <span className="text-lg md:text-lg font-bold pb-3 md:pb-4 opacity-70">:</span>

            <div className="flex flex-col items-center">
              <span className="bg-black/20 backdrop-blur-sm px-1.5 md:px-2 py-1 rounded-lg text-base md:text-xl font-extrabold min-w-[2rem] md:min-w-[3rem] tracking-wider text-center shadow-inner border border-white/10 text-white">
                {String(timeLeft.minutes).padStart(2, '0')}
              </span>
              <span className="text-[9px] md:text-md opacity-90 mt-1 font-bold">دقائق</span>
            </div>

            <span className="text-lg md:text-lg font-bold pb-3 md:pb-4 opacity-70">:</span>

            <div className="flex flex-col items-center">
              <span className="bg-black/20 backdrop-blur-sm px-1.5 md:px-2 py-1 rounded-lg text-base md:text-xl font-extrabold min-w-[2rem] md:min-w-[3rem] tracking-wider text-center shadow-inner border border-white/10 text-white">
                {String(timeLeft.hours).padStart(2, '0')}
              </span>
              <span className="text-[9px] md:text-md opacity-90 mt-1 font-bold">ساعات</span>
            </div>

            <span className="text-lg md:text-lg font-bold pb-3 md:pb-4 opacity-70">:</span>

            <div className="flex flex-col items-center">
              <span className="bg-black/20 backdrop-blur-sm px-1.5 md:px-2 py-1 rounded-lg text-base md:text-xl font-extrabold min-w-[2rem] md:min-w-[3rem] tracking-wider text-center shadow-inner border border-white/10 text-white">
                {String(timeLeft.days).padStart(2, '0')}
              </span>
              <span className="text-[9px] md:text-md opacity-90 mt-1 font-bold">أيام</span>
            </div>
          </div>

          {/* Action Button */}
          <a href="#pricing" className="flex items-center justify-center gap-1.5 bg-white text-[#e00000] hover:bg-slate-50 px-5 md:px-6 py-2 rounded-full text-lg md:text-base font-extrabold transition-all duration-300 hover:scale-[1.03] shadow-[0_0_15px_rgba(255,255,255,0.3)] hover:shadow-[0_0_20px_rgba(255,255,255,0.5)] xl:mr-auto shrink-0 border border-white w-full md:w-auto">
            اغتنم الفرصة
            <ArrowLeft className="w-4 h-4 md:w-4 md:h-4" />
          </a>
        </div>

      </div>
    </div>
  );
}
